import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Target, TrendingUp, TrendingDown, Calendar, RefreshCcw,
  CheckCircle, AlertTriangle, Loader2, Award, Users, 
  DollarSign, UserPlus, MessageSquare, UserCheck, Layers
} from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiUrl';

const MyTargetsPage = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [targets, setTargets] = useState({ daily: null, weekly: null, monthly: null });
  const [shortfalls, setShortfalls] = useState({ daily: null, weekly: null, monthly: null });
  const [config, setConfig] = useState(null);
  const [periodType, setPeriodType] = useState('daily');

  // Fetch user's targets
  const fetchTargets = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/target-definition/definitions?user_id=${user.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const daily = data.find(d => d.period_type === 'daily');
        const weekly = data.find(d => d.period_type === 'weekly');
        const monthly = data.find(d => d.period_type === 'monthly');
        setTargets({ daily, weekly, monthly });
      }
    } catch (err) {
      console.error('Error fetching targets:', err);
    }
  }, [token, user.id]);

  // Fetch shortfalls
  const fetchShortfalls = useCallback(async () => {
    try {
      const periods = ['daily', 'weekly', 'monthly'];
      const results = {};
      
      for (const period of periods) {
        const res = await fetch(`${API_URL}/target-definition/shortfall?period_type=${period}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          results[period] = await res.json();
        }
      }
      setShortfalls(results);
    } catch (err) {
      console.error('Error fetching shortfalls:', err);
    }
  }, [token]);

  // Fetch config
  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/target-definition/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setConfig(await res.json());
      }
    } catch (err) {
      console.error('Error fetching config:', err);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchTargets(), fetchShortfalls(), fetchConfig()]);
      setLoading(false);
    };
    loadData();
  }, [fetchTargets, fetchShortfalls, fetchConfig]);

  const handleRefresh = async () => {
    setLoading(true);
    await Promise.all([fetchTargets(), fetchShortfalls()]);
    setLoading(false);
    toast.success('Data refreshed');
  };

  // Format currency
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '₹0';
    return `₹${Number(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  // Get progress bar
  const getProgressBar = (percent) => {
    const color = percent >= 100 ? 'bg-emerald-500' : percent >= 75 ? 'bg-amber-500' : 'bg-red-500';
    return (
      <div className="w-20 bg-slate-700 rounded-full h-2">
        <div className={`h-2 rounded-full ${color}`} style={{ width: `${Math.min(percent || 0, 100)}%` }} />
      </div>
    );
  };

  // Get current shortfall data
  const currentShortfall = shortfalls[periodType] || {};
  const sf = currentShortfall.shortfalls || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C5A059]" />
      </div>
    );
  }

  const noTargets = !targets.daily && !targets.weekly && !targets.monthly;

  return (
    <div className="space-y-6" data-testid="my-targets-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white">My Targets & Shortfall</h1>
          <p className="text-slate-400">Track your performance against assigned targets</p>
        </div>
        <div className="flex gap-2">
          <Select value={periodType} onValueChange={setPeriodType}>
            <SelectTrigger className="w-40 bg-slate-800 border-white/10">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Last Trading Day</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleRefresh} className="border-[#C5A059]/30">
            <RefreshCcw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {noTargets ? (
        <Card className="card-dark border-white/5">
          <CardContent className="py-12 text-center">
            <Target className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-white text-lg font-medium mb-2">No Targets Assigned</h3>
            <p className="text-slate-400">Contact your manager to set up your targets.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Shortfall Overview Section - Same format as Principal */}
          <Card className="card-dark border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-[#C5A059]" />
                My Shortfall Overview - {periodType === 'daily' ? 'Last Trading Day' : periodType.charAt(0).toUpperCase() + periodType.slice(1)}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-slate-400">Metric</TableHead>
                    <TableHead className="text-slate-400 text-center">Target</TableHead>
                    <TableHead className="text-slate-400 text-center">Achieved</TableHead>
                    <TableHead className="text-slate-400 text-center">Shortfall</TableHead>
                    <TableHead className="text-slate-400 text-center">Progress</TableHead>
                    <TableHead className="text-slate-400 text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Traded Clients */}
                  <TableRow className="border-white/5">
                    <TableCell className="text-white font-medium flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-400" /> Traded Clients
                    </TableCell>
                    <TableCell className="text-center text-white">{sf.traded_clients?.target || 0}</TableCell>
                    <TableCell className="text-center text-white">{sf.traded_clients?.achieved || 0}</TableCell>
                    <TableCell className="text-center">
                      {sf.traded_clients?.shortfall > 0 ? (
                        <span className="text-red-400">-{sf.traded_clients.shortfall}</span>
                      ) : (
                        <span className="text-emerald-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getProgressBar(sf.traded_clients?.percent_achieved)}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      {(sf.traded_clients?.percent_achieved || 0) >= 100 ? (
                        <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Done</Badge>
                      ) : (
                        <Badge className="bg-red-600"><AlertTriangle className="w-3 h-3 mr-1" />{sf.traded_clients?.percent_achieved || 0}%</Badge>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Brokerage */}
                  <TableRow className="border-white/5">
                    <TableCell className="text-white font-medium flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-[#C5A059]" /> Brokerage
                    </TableCell>
                    <TableCell className="text-center text-white">{formatCurrency(sf.brokerage?.target)}</TableCell>
                    <TableCell className="text-center text-white">{formatCurrency(sf.brokerage?.achieved)}</TableCell>
                    <TableCell className="text-center">
                      {sf.brokerage?.shortfall > 0 ? (
                        <span className="text-red-400">-{formatCurrency(sf.brokerage.shortfall)}</span>
                      ) : (
                        <span className="text-emerald-400">₹0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getProgressBar(sf.brokerage?.percent_achieved)}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      {(sf.brokerage?.percent_achieved || 0) >= 100 ? (
                        <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Done</Badge>
                      ) : (
                        <Badge className="bg-red-600"><AlertTriangle className="w-3 h-3 mr-1" />{sf.brokerage?.percent_achieved || 0}%</Badge>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Client Activation */}
                  <TableRow className="border-white/5">
                    <TableCell className="text-white font-medium flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-purple-400" /> Client Activation
                    </TableCell>
                    <TableCell className="text-center text-white">{sf.client_activation?.target || 0}</TableCell>
                    <TableCell className="text-center text-white">{sf.client_activation?.achieved || 0}</TableCell>
                    <TableCell className="text-center">
                      {sf.client_activation?.shortfall > 0 ? (
                        <span className="text-red-400">-{sf.client_activation.shortfall}</span>
                      ) : (
                        <span className="text-emerald-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getProgressBar(sf.client_activation?.percent_achieved)}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      {(sf.client_activation?.percent_achieved || 0) >= 100 ? (
                        <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Done</Badge>
                      ) : (
                        <Badge className="bg-red-600"><AlertTriangle className="w-3 h-3 mr-1" />{sf.client_activation?.percent_achieved || 0}%</Badge>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Tier Trade (All periods) */}
                  {sf.tier_trade && (
                    <TableRow className="border-white/5">
                      <TableCell className="text-white font-medium flex items-center gap-2">
                        <Layers className="w-4 h-4 text-cyan-400" /> Tier Trade
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="grid grid-cols-4 gap-1 text-xs">
                          <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">P:{sf.tier_trade.platinum?.target || 0}</span>
                          <span className="px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">G:{sf.tier_trade.gold?.target || 0}</span>
                          <span className="px-1.5 py-0.5 rounded bg-slate-500/10 text-gray-300 border border-slate-500/20">S:{sf.tier_trade.silver?.target || 0}</span>
                          <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">B:{sf.tier_trade.blue?.target || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="grid grid-cols-4 gap-1 text-xs">
                          <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">P:{sf.tier_trade.platinum?.achieved || 0}</span>
                          <span className="px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">G:{sf.tier_trade.gold?.achieved || 0}</span>
                          <span className="px-1.5 py-0.5 rounded bg-slate-500/10 text-gray-300 border border-slate-500/20">S:{sf.tier_trade.silver?.achieved || 0}</span>
                          <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">B:{sf.tier_trade.blue?.achieved || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-1 justify-center text-xs flex-wrap">
                          {sf.tier_trade.platinum?.shortfall > 0 && <span className="text-red-400">P:-{sf.tier_trade.platinum.shortfall}</span>}
                          {sf.tier_trade.gold?.shortfall > 0 && <span className="text-red-400">G:-{sf.tier_trade.gold.shortfall}</span>}
                          {sf.tier_trade.silver?.shortfall > 0 && <span className="text-red-400">S:-{sf.tier_trade.silver.shortfall}</span>}
                          {sf.tier_trade.blue?.shortfall > 0 && <span className="text-red-400">B:-{sf.tier_trade.blue.shortfall}</span>}
                          {!sf.tier_trade.platinum?.shortfall && !sf.tier_trade.gold?.shortfall && !sf.tier_trade.silver?.shortfall && !sf.tier_trade.blue?.shortfall && (
                            <span className="text-emerald-400">0</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">-</TableCell>
                      <TableCell className="text-center">
                        <Badge className="bg-slate-600">Tier-wise</Badge>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* New Clients */}
                  <TableRow className="border-white/5">
                    <TableCell className="text-white font-medium flex items-center gap-2">
                      <UserPlus className="w-4 h-4 text-green-400" /> New Clients
                    </TableCell>
                    <TableCell className="text-center text-white">{sf.new_clients?.target || 0}</TableCell>
                    <TableCell className="text-center text-white">{sf.new_clients?.achieved || 0}</TableCell>
                    <TableCell className="text-center">
                      {sf.new_clients?.shortfall > 0 ? (
                        <span className="text-red-400">-{sf.new_clients.shortfall}</span>
                      ) : (
                        <span className="text-emerald-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getProgressBar(sf.new_clients?.percent_achieved)}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      {(sf.new_clients?.percent_achieved || 0) >= 100 ? (
                        <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Done</Badge>
                      ) : sf.new_clients?.target > 0 ? (
                        <Badge className="bg-red-600"><AlertTriangle className="w-3 h-3 mr-1" />{sf.new_clients?.percent_achieved || 0}%</Badge>
                      ) : (
                        <Badge className="bg-slate-600">N/A</Badge>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Meetings */}
                  <TableRow className="border-white/5">
                    <TableCell className="text-white font-medium flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-cyan-400" /> Meetings
                    </TableCell>
                    <TableCell className="text-center text-white">{sf.meetings?.target || 0}</TableCell>
                    <TableCell className="text-center text-white">{sf.meetings?.achieved || 0}</TableCell>
                    <TableCell className="text-center">
                      {sf.meetings?.shortfall > 0 ? (
                        <span className="text-red-400">-{sf.meetings.shortfall}</span>
                      ) : sf.meetings?.excess > 0 ? (
                        <span className="text-emerald-400">+{sf.meetings.excess}</span>
                      ) : (
                        <span className="text-emerald-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex justify-center">{getProgressBar(sf.meetings?.percent_achieved)}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      {(sf.meetings?.percent_achieved || 0) >= 100 ? (
                        <Badge className="bg-emerald-600"><CheckCircle className="w-3 h-3 mr-1" />Done</Badge>
                      ) : sf.meetings?.target > 0 ? (
                        <Badge className="bg-red-600"><AlertTriangle className="w-3 h-3 mr-1" />{sf.meetings?.percent_achieved || 0}%</Badge>
                      ) : (
                        <Badge className="bg-slate-600">N/A</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Card className="card-dark border-white/5">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-xs text-slate-400">Traded Gap</p>
                    <p className="text-lg font-bold text-blue-400">{sf.traded_clients?.shortfall || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-dark border-white/5">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#C5A059]" />
                  <div>
                    <p className="text-xs text-slate-400">Brokerage Gap</p>
                    <p className="text-lg font-bold text-[#C5A059]">{formatCurrency(sf.brokerage?.shortfall)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-dark border-white/5">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-purple-500" />
                  <div>
                    <p className="text-xs text-slate-400">Activation Gap</p>
                    <p className="text-lg font-bold text-purple-400">{sf.client_activation?.shortfall || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-dark border-white/5">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-xs text-slate-400">New Clients Gap</p>
                    <p className="text-lg font-bold text-green-400">{sf.new_clients?.shortfall || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="card-dark border-white/5">
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-cyan-500" />
                  <div>
                    <p className="text-xs text-slate-400">Meetings Gap</p>
                    <p className="text-lg font-bold text-cyan-400">{sf.meetings?.shortfall || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className={`card-dark ${currentShortfall.overall_status === 'green' ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2">
                  {currentShortfall.overall_status === 'green' ? (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  )}
                  <div>
                    <p className="text-xs text-slate-400">Overall Status</p>
                    <p className={`text-lg font-bold ${currentShortfall.overall_status === 'green' ? 'text-emerald-400' : 'text-red-400'}`}>
                      {currentShortfall.overall_status === 'green' ? 'On Track' : 'Shortfall'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Base Metrics */}
          {targets.daily && (
            <Card className="card-dark border-white/5">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#C5A059]" />
                  My Base Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-slate-800 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Total AUM</p>
                    <p className="text-[#C5A059] text-2xl font-bold">
                      {formatCurrency(targets.daily.targets?.brokerage?.base_aum)}
                    </p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Active Clients</p>
                    <p className="text-white text-2xl font-bold">
                      {targets.daily.targets?.traded_clients?.base_value || 0}
                    </p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Dormant Pool</p>
                    <p className="text-white text-2xl font-bold">
                      {targets.daily.targets?.client_activation?.dormant_pool || 0}
                    </p>
                  </div>
                  <div className="bg-slate-800 rounded-lg p-4">
                    <p className="text-slate-400 text-sm">Role</p>
                    <p className="text-white text-2xl font-bold uppercase">
                      {user.role}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default MyTargetsPage;
